'use client';
import { ArticleComponent } from '@hyphengroup/cms-components/components/Article';
import { TheWrapper } from '@hyphengroup/cms-components/components/TheWrapper';
import classNames from 'classnames';
import { useRef, useState } from 'react';
import { getCmsPageLink } from '../../../../app/[site]/[locale]/(cms)/_modules/routing';
import { useClientContext } from '../../../../modules/context/client';
import { Paginator } from '../../components-internal/Paginator';
import { formatDatePageTitle, getContentUpdatedDate } from '../../helpers';
import { pushDataLayer } from '../../pushDataLayer';
import { DEFAULT_ARTICLES_COUNT } from './const';
import {
  CmsArticleCard,
  ComponentVerticalArticles,
} from '$/(cms)/_modules/cmsService/types';

const PAGINATION_SIZE = 10;

export const VerticalArticlesClient: React.FC<
  ComponentVerticalArticles & {
    initialArticles: CmsArticleCard[];
    wrapInTheWrapper?: boolean;
    // DOM id of the outer container. Defaults to "vertical-articles" (the landing-page anchor);
    // a second instance on the same page (e.g. auto related-articles) must override it so the id
    // stays unique.
    domId?: string;
    // show the category + updated-date meta line on each card. Off by default so the existing
    // vertical-landing and author-page consumers are unchanged; the CMS-1908 blog surfaces
    // (tag archive, auto related-articles) opt in.
    showMeta?: boolean;
  }
> = ({
  title,
  id,
  initialArticles,
  countDisplayArticles,
  showAllArticles,
  wrapInTheWrapper = true,
  domId = 'vertical-articles',
  showMeta = false,
}) => {
  const [pageNumber, setPageNumber] = useState(1);
  const { langPrefix, brand, lang } = useClientContext();

  // Through the shared helper rather than off `updatedAt`, which moves whenever
  // the row is written and holds the migration date for everything brought over
  // from HubSpot — the card would otherwise disagree with the order the list is
  // now sorted in, and with the date the article's own page shows.
  const dateFor = (article: CmsArticleCard) => {
    if (!showMeta) return null;
    const updated = getContentUpdatedDate(article);
    return updated ? formatDatePageTitle(updated, lang) : null;
  };

  const pagedArticles = showAllArticles
    ? initialArticles.slice(
        (pageNumber - 1) * PAGINATION_SIZE,
        pageNumber * PAGINATION_SIZE,
      )
    : initialArticles.slice(0, countDisplayArticles ?? DEFAULT_ARTICLES_COUNT);

  const pageCount = showAllArticles
    ? Math.ceil(initialArticles.length / PAGINATION_SIZE)
    : 1;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const changePage = (pageNumber: number) => {
    if (!containerRef.current) return;
    window.scrollTo({
      behavior: 'smooth',
      top: containerRef.current.offsetTop,
    });

    setPageNumber(pageNumber);
  };

  const content = (
    <div className="flex flex-col gap-spacing/gap/y" ref={containerRef}>
      <div className={classNames('grid grid-cols-2 gap-spacing/gap/x')}>
        {pagedArticles.map((article) => (
          <div
            key={article.documentId}
            className="w-full rounded-radius/card border border-borders/default bg-fill/card"
          >
            <ArticleComponent
              url={getCmsPageLink({
                brand,
                pageType: 'article',
                slug: article.slug || '',
                vertical: article.section,
                langPrefix,
                urlPrefix: article.urlPrefix,
              })}
              title={article.title}
              subtitle={article.subtitle || null}
              imageUrl={article.image?.[0]?.url}
              category={showMeta ? article.page_category || null : null}
              date={dateFor(article)}
              pushDataLayer={pushDataLayer}
            />
          </div>
        ))}
      </div>

      {showAllArticles && pageCount > 1 && (
        <Paginator
          count={pageCount}
          current={pageNumber}
          onChange={changePage}
        />
      )}
    </div>
  );

  return (
    <div className="mx-auto max-w-screen-2xl" id={domId}>
      {wrapInTheWrapper ? (
        <TheWrapper type="simple" title={title ?? ''} id={id}>
          {content}
        </TheWrapper>
      ) : (
        content
      )}
    </div>
  );
};
